import { delay } from "../../utils";
import { Deferred } from "../../../Helpers/Deferred";
import { getCandyHub } from "../../../Forms/Sentry/InitializeSentry";
import { runAsyncAction } from "../../../../../Common/TypingUtils";
import { isAbortException } from "../../../../../Common/Errors";

export function runCalculatorFloatingAsyncAction(action: () => Promise<void>): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    action();
}

export function createSimpleAsyncActionQueue(errorHandler: (e: unknown | Error) => void, queueDelay?: number) {
    const activeTaskQueue: Array<() => Promise<void>> = [];
    const enqueue = (action: () => Promise<void>) => {
        activeTaskQueue.push(action);
        if (activeTaskQueue.length === 1) {
            runCalculatorFloatingAsyncAction(async () => {
                while (activeTaskQueue.length > 0) {
                    const currentTask = activeTaskQueue[0];
                    if (currentTask != undefined) {
                        try {
                            await currentTask();
                        } catch (e) {
                            errorHandler(e);
                        }
                        if (queueDelay != undefined) {
                            await delay(queueDelay);
                        }
                    }
                    activeTaskQueue.shift();
                }
            });
        }
    };

    const enqueueWithPromise = <TResult>(action: () => Promise<TResult>) => {
        const deferred = new Deferred<TResult>();
        const wrappedAction = async () => {
            try {
                deferred.resolve(await action());
            } catch (e) {
                deferred.reject(e);
            }
        };
        enqueue(wrappedAction);
        return deferred.promise;
    };

    return {
        enqueue: enqueue,
        enqueueWithPromise: enqueueWithPromise,
        getQueueLength: () => activeTaskQueue.length,
    };
}

export function createActionQueue(name: string, queueDelay?: number) {
    return createSimpleAsyncActionQueue(
        error => getCandyHub().captureException(error, { data: { queueName: name } }),
        queueDelay
    );
}

const defaultErrorLogger = (error: unknown) => getCandyHub().captureException(error);
export function createAutoCancellableAsyncQueue(errorLogger: (error: unknown) => void = defaultErrorLogger) {
    let currentTask = Promise.resolve();
    let currentAbortController = new AbortController();
    let currentIsNonCancellable = false;
    let currentCategory = "default";
    const workerFunc = async (
        func: (signal: AbortSignal) => Promise<void>,
        isNonCancellable = false,
        category = "default"
    ) => {
        const deferred = new Deferred<void>();
        const prevTask = currentTask;
        const prevAbortController = currentAbortController;
        const prevIsNonCancellable = currentIsNonCancellable;
        const prevCategory = currentCategory;
        currentTask = deferred.promise;
        currentIsNonCancellable = isNonCancellable;
        currentCategory = category;
        currentAbortController = new AbortController();
        const signal = currentAbortController.signal;

        try {
            if (!prevIsNonCancellable && prevCategory === currentCategory) {
                prevAbortController.abort();
            }
            await prevTask;
        } catch (error) {
            if (!isAbortException(error)) {
                errorLogger?.(error);
            }
        }

        try {
            if (!signal.aborted) {
                await func(signal);
            }
            deferred.resolve();
        } catch (error) {
            deferred.reject(error);
        }
    };
    return {
        enqueue: (func: (signal: AbortSignal) => Promise<void>, isNonCancellable = false, category?: string) =>
            runAsyncAction(() => workerFunc(func, isNonCancellable, category)),
        enqueueWithPromise: (func: (signal: AbortSignal) => Promise<void>, isNonCancellable = false) =>
            workerFunc(func, isNonCancellable),
        abortCurrent: () => currentAbortController.abort(),
        awaitCurrent: async () => await currentTask,
    };
}
